import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faEye, 
  faFileInvoiceDollar,
  faFilter
} from '@fortawesome/free-solid-svg-icons';
import { ADMIN_URLS } from '../config/apiConfig';
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";


function PaymentsList() {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    paymentType: '',
    status: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [memberDetails, setMemberDetails] = useState({});
  
  useEffect(() => {
    fetchPayments();
  }, [currentPage, filter]);
  
  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('page', currentPage);
      
      // Add filters if they exist
      if (filter.paymentType) {
        queryParams.append('payment_type', filter.paymentType);
      }
      
      if (filter.status) {
        queryParams.append('status', filter.status);
      }
      
      // Add search term if it exists
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      // Make the API request using the URL from apiConfig
      const response = await fetch(`${ADMIN_URLS.PAYMENTS}?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Update state with response data
      const paymentsData = data.items || [];
      setPayments(paymentsData);
      setTotalPages(data.total_pages || 1);
      
      // Fetch member details for all payments
      fetchMemberDetails(paymentsData);
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPayments([]);
      setIsLoading(false);
    }
  };
  
  const fetchMemberDetails = async (paymentsData) => {
    try {
      // Create a Set of unique member IDs to avoid duplicate requests
      const memberIds = [...new Set(paymentsData.map(payment => payment.member_id))];
      
      // Store member details in a temporary object
      const memberData = { ...memberDetails };
      
      // Fetch member details for each unique member ID
      for (const memberId of memberIds) {
        // Skip if we already have this member's details
        if (memberData[memberId]) continue;
        
        const response = await fetch(`${ADMIN_URLS.MEMBER_DETAIL(memberId)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        });
        
        if (response.ok) {
          const memberInfo = await response.json();
          memberData[memberId] = {
            name: `${memberInfo.firstName || ''} ${memberInfo.lastName || ''}`.trim(),
            email: memberInfo.email || '',
            phone: memberInfo.phone || 'N/A'
          };
        }
      }
      
      // Update state with all member details
      setMemberDetails(memberData);
    } catch (error) {
      console.error('Error fetching member details:', error);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchPayments();
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value
    });
    setCurrentPage(1); // Reset to first page on filter change
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get member info from the cached details
  const getMemberInfo = (memberId) => {
    return memberDetails[memberId] || { name: 'Loading...', email: '', phone: 'Loading...' };
  };

  const handleMarkCompleted = async (paymentId) => {
    if (!window.confirm("Mark this payment as completed?")) return;

    try {
      const res = await fetch(`${ADMIN_URLS.MARK_AS_COMPLETE_PAYMENTS(paymentId)}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("adminToken")}`,
        }
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Failed");
        return;
      }

      fetchPayments();
    } catch (err) {
      console.error(err);
    }
  };



  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Payments</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Payments</span>
          </li>
        </ul>
      </div>
      
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-body" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'relative', marginRight: '1rem' }}>
                <input 
                  type="text" 
                  placeholder="Search payments..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    padding: '0.5rem 0.5rem 0.5rem 2rem', 
                    borderRadius: '4px', 
                    border: '1px solid var(--admin-border)',
                    minWidth: '250px'
                  }}
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    position: 'absolute', 
                    left: '0.5rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }}
                />
              </div>
              <button 
                type="submit"
                className="admin-btn admin-btn-primary"
                style={{ padding: '0.5rem 1rem' }}
              >
                Search
              </button>
            </form>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <select
                name="paymentType"
                value={filter.paymentType}
                onChange={handleFilterChange}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid var(--admin-border)'
                }}
              >
                <option value="">All Types</option>
                <option value="registration">Membership</option>
                <option value="program_registration">Program</option>
                <option value="professional_listing">Listing</option>
              </select>
              
              <select
                name="status"
                value={filter.status}
                onChange={handleFilterChange}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid var(--admin-border)'
                }}
              >
                <option value="">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      <div className="admin-table-container">
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Member</th>
                <th>Phone</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Status</th>
                <th>Date</th>
                <th>Transaction ID</th>
              </tr>
            </thead>
            <tbody>
              {payments.length > 0 ? (
                payments.map((payment) => {
                  const member = getMemberInfo(payment.member_id);
                  return (
                    <tr key={payment.id}>
                      <td>{payment.id}</td>
                      <td>
                        <div>{member.name}</div>
                        <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>{member.email}</div>
                      </td>
                      <td>{member.phone}</td>
                      <td style={{ fontWeight: '600' }}>{formatCurrency(payment.amount)}</td>
                      <td style={{ textTransform: 'capitalize' }}>{payment.payment_type}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span 
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              backgroundColor: 
                                payment.status === 'completed' ? '#d4edda' : 
                                payment.status === 'pending' ? '#fff3cd' : 
                                payment.status === 'failed' ? '#f8d7da' :
                                '#d1ecf1',
                              color: 
                                payment.status === 'completed' ? '#155724' : 
                                payment.status === 'pending' ? '#856404' : 
                                payment.status === 'failed' ? '#721c24' :
                                '#0c5460'
                            }}
                          >
                            {payment.status}
                          </span>
                          {payment.status === "pending" && payment.payment_type === "registration" && (
                            <button
                              onClick={() => handleMarkCompleted(payment.id)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.4rem',
                                padding: '0.4rem 0.75rem',
                                fontSize: '0.813rem',
                                fontWeight: '500',
                                color: '#fff',
                                backgroundColor: '#28a745',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(40, 167, 69, 0.2)',
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#218838';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(40, 167, 69, 0.3)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = '#28a745';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(40, 167, 69, 0.2)';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                              onMouseDown={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                              title="Mark this payment as completed"
                            >
                              <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '0.875rem' }} />
                              <span>Complete</span>
                            </button>
                          )}
                        </div>
                      </td>
                      <td>{formatDate(payment.payment_date)}</td>
                      <td>{payment.reference_id || 'N/A'}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>
                    No payments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        
        {totalPages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            padding: '1rem', 
            borderTop: '1px solid var(--admin-border)'
          }}>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button 
                className="admin-btn"
                style={{ 
                  backgroundColor: '#f8f9fa', 
                  color: 'var(--admin-dark)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(page => Math.max(page - 1, 1))}
              >
                &laquo;
              </button>
              
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className="admin-btn"
                  style={{ 
                    backgroundColor: currentPage === i + 1 ? 'var(--admin-primary)' : '#f8f9fa',
                    color: currentPage === i + 1 ? 'white' : 'var(--admin-dark)',
                    padding: '0.5rem 0.75rem'
                  }}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              
              <button 
                className="admin-btn"
                style={{ 
                  backgroundColor: '#f8f9fa',
                  color: 'var(--admin-dark)',
                  padding: '0.5rem 0.75rem'
                }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(page => Math.min(page + 1, totalPages))}
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default PaymentsList;
